import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { AdminLayout } from '../components/AdminLayout';
import apiService from '../services/api';
import { Plan } from '../types';
import {
  Plus,
  Edit,
  Trash2,
  Save,
  X,
  Building2,
  Users,
  Gift,
  BarChart3,
  Mail,
  Shield,
  Layers,
  Workflow,
  Link2,
  Brain,
  Headphones
} from 'lucide-react';

const Container = styled.div`
  padding: 2rem;
  width: 100%;
  max-width: none;
  margin: 0;

  @media (max-width: 768px) {
    padding: 1.25rem 1rem;
  }
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 1rem;
  }
`;

const Title = styled.h1`
  font-size: 2rem;
  font-weight: 700;
  color: #333;

  @media (max-width: 768px) {
    font-size: 1.5rem;
  }
`;

const Button = styled.button<{ $variant?: 'primary' | 'secondary' | 'danger' | 'success' }>`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-weight: 600;
  transition: all 0.2s ease;

  ${props => {
    switch (props.$variant) {
      case 'primary':
        return `
          background: linear-gradient(135deg, #00d4aa 0%, #00b894 100%);
          color: white;
          &:hover {
            transform: translateY(-2px);
            box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
          }
        `;
      case 'success':
        return `
          background: #28a745;
          color: white;
          &:hover {
            background: #218838;
          }
        `;
      case 'danger':
        return `
          background: #dc3545;
          color: white;
          &:hover {
            background: #c82333;
          }
        `;
      default:
        return `
          background: white;
          color: #333;
          border: 1px solid #e9ecef;
          &:hover {
            background: #f8f9fa;
          }
        `;
    }
  }}

  @media (max-width: 768px) {
    width: 100%;
    justify-content: center;
  }
`;

const PlansGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 2rem;
  margin-bottom: 3rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }
`;

const PlanCard = styled.div<{ $featured?: boolean }>`
  background: white;
  border: 2px solid ${props => props.$featured ? '#00d4aa' : '#e9ecef'};
  border-radius: 16px;
  padding: 2rem;
  position: relative;
  transition: all 0.2s ease;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 30px rgba(0, 0, 0, 0.15);
  }

  @media (max-width: 768px) {
    padding: 1.5rem;
  }
`;

const PlanBadge = styled.div`
  position: absolute;
  top: -12px;
  left: 50%;
  transform: translateX(-50%);
  background: linear-gradient(135deg, #00d4aa 0%, #00b894 100%);
  color: white;
  padding: 0.5rem 1.5rem;
  border-radius: 20px;
  font-size: 0.875rem;
  font-weight: 600;
`;

const PlanHeader = styled.div`
  text-align: center;
  margin-bottom: 2rem;
`;

const PlanName = styled.h3`
  font-size: 1.5rem;
  font-weight: 700;
  color: #333;
  margin-bottom: 0.5rem;
`;

const PlanPrice = styled.div`
  font-size: 2.5rem;
  font-weight: 700;
  color: #00d4aa;
  margin-bottom: 1rem;
`;

const PlanDescription = styled.p`
  color: #666;
  font-size: 0.875rem;
  line-height: 1.5;
`;

const FeaturesList = styled.div`
  margin-bottom: 2rem;
`;

const FeatureItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.5rem 0;
  color: #666;
  font-size: 0.875rem;
`;

const FeatureIcon = styled.div`
  width: 20px;
  height: 20px;
  border-radius: 4px;
  background: #28a745;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const PlanActions = styled.div`
  display: flex;
  gap: 0.75rem;
  justify-content: center;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const Modal = styled.div<{ $show: boolean }>`
  display: ${props => props.$show ? 'flex' : 'none'};
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  z-index: 1000;
  align-items: center;
  justify-content: center;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 16px;
  padding: 2rem;
  width: 90%;
  max-width: 600px;
  max-height: 90vh;
  overflow-y: auto;

  @media (max-width: 768px) {
    padding: 1.5rem;
  }
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.75rem;
  }
`;

const ModalTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 700;
  color: #333;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Label = styled.label`
  font-weight: 600;
  color: #333;
  font-size: 0.875rem;
`;

const Input = styled.input<{ $hasError?: boolean }>`
  padding: 0.75rem;
  border: 2px solid ${props => props.$hasError ? '#dc3545' : '#e9ecef'};
  border-radius: 8px;
  font-size: 1rem;
  transition: border-color 0.2s ease;

  &:focus {
    outline: none;
    border-color: #00d4aa;
  }
`;

const Textarea = styled.textarea<{ $hasError?: boolean }>`
  padding: 0.75rem;
  border: 2px solid ${props => props.$hasError ? '#dc3545' : '#e9ecef'};
  border-radius: 8px;
  font-size: 1rem;
  min-height: 100px;
  resize: vertical;
  transition: border-color 0.2s ease;

  &:focus {
    outline: none;
    border-color: #00d4aa;
  }
`;

const Checkbox = styled.input`
  margin-right: 0.5rem;
`;

const CheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  color: #333;
  font-size: 0.875rem;
  cursor: pointer;
`;

const ErrorMessage = styled.span`
  color: #dc3545;
  font-size: 0.875rem;
`;

const AlertBox = styled.div<{ $type: 'success' | 'error' }>`
  position: fixed;
  top: 2rem;
  right: 2rem;
  padding: 1rem 1.5rem;
  background: ${props => props.$type === 'success' ? '#d4edda' : '#f8d7da'};
  color: ${props => props.$type === 'success' ? '#155724' : '#721c24'};
  border: 1px solid ${props => props.$type === 'success' ? '#c3e6cb' : '#f5c6cb'};
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 2000;
  animation: slideIn 0.3s ease-out;
  max-width: 400px;
  font-weight: 500;

  @media (max-width: 768px) {
    right: 1rem;
    left: 1rem;
    top: 1rem;
    max-width: none;
  }

  @keyframes slideIn {
    from {
      transform: translateX(100%);
      opacity: 0;
    }
    to {
      transform: translateX(0);
      opacity: 1;
    }
  }
`;

const ConfirmModalOverlay = styled.div<{ $show: boolean }>`
  display: ${props => props.$show ? 'flex' : 'none'};
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  z-index: 1500;
  align-items: center;
  justify-content: center;
`;

const ConfirmBox = styled.div`
  background: white;
  border-radius: 12px;
  padding: 2rem;
  max-width: 450px;
  width: 90%;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);

  @media (max-width: 768px) {
    padding: 1.5rem;
  }
`;

const ConfirmTitle = styled.h3`
  margin: 0 0 1rem 0;
  color: #333;
  font-size: 1.25rem;
  font-weight: 600;
`;

const ConfirmText = styled.p`
  margin: 0 0 1.5rem 0;
  color: #666;
  line-height: 1.5;
`;

const ConfirmActions = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: flex-end;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const initialPlan: Omit<Plan, 'id' | 'created_at' | 'updated_at'> = {
  name: '',
  description: '',
  price: 0,
  max_stores: 1,
  max_employees_per_store: 1,
  max_customers: 100,
  max_rewards: 10,
  has_advanced_reports: false,
  has_api_access: false,
  has_mobile_app_access: true,
  has_crm: false,
  has_custom_smtp: false,
  has_tier_program: false,
  has_customer_automation: false,
  has_partner_network: false,
  has_ai_insights: false,
  has_priority_support: false,
  is_active: true
};

export const AdminPlansPage: React.FC = () => {
  const [plans, setPlans] = useState<Plan[]>([]);
  const [showModal, setShowModal] = useState(false);
  const [editingPlan, setEditingPlan] = useState<Plan | null>(null);
  const [formData, setFormData] = useState(initialPlan);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);
  const [planToDelete, setPlanToDelete] = useState<number | null>(null);
  const [alertMessage, setAlertMessage] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  useEffect(() => {
    loadPlans();
  }, []);

  const loadPlans = async () => {
    try {
      const response = await apiService.getPlans();
      if (response.data) {
        setPlans(response.data);
      }
    } catch (error) {
      console.error('Erro ao carregar planos:', error);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value
    }));
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Nome é obrigatório';
    }

    if (formData.price < 0) {
      newErrors.price = 'Preço não pode ser negativo';
    }

    if (formData.max_stores <= 0) {
      newErrors.max_stores = 'Deve permitir pelo menos 1 loja';
    }

    if (formData.max_employees_per_store <= 0) {
      newErrors.max_employees_per_store = 'Deve permitir pelo menos 1 funcionário por loja';
    }

    if (formData.max_customers <= 0) {
      newErrors.max_customers = 'Deve permitir pelo menos 1 cliente';
    }

    if (formData.max_rewards <= 0) {
      newErrors.max_rewards = 'Deve permitir pelo menos 1 recompensa';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      if (editingPlan) {
        // Atualizar plano existente
        await apiService.updatePlan(editingPlan.id, formData);
        setAlertMessage({ type: 'success', message: 'Plano atualizado com sucesso!' });
      } else {
        // Criar novo plano
        await apiService.createPlan(formData);
        setAlertMessage({ type: 'success', message: 'Plano criado com sucesso!' });
      }
      
      await loadPlans();
      handleCloseModal();
      setTimeout(() => setAlertMessage(null), 3000);
    } catch (error) {
      console.error('Erro ao salvar plano:', error);
      setAlertMessage({ type: 'error', message: 'Erro ao salvar plano. Verifique os dados e tente novamente.' });
      setTimeout(() => setAlertMessage(null), 5000);
    }
  };

  const handleEditPlan = (plan: Plan) => {
    setEditingPlan(plan);
    setFormData({
      name: plan.name,
      description: plan.description,
      price: plan.price,
      max_stores: plan.max_stores,
      max_employees_per_store: plan.max_employees_per_store,
      max_customers: plan.max_customers,
      max_rewards: plan.max_rewards,
      has_advanced_reports: plan.has_advanced_reports,
      has_api_access: plan.has_api_access,
      has_mobile_app_access: true,
      has_crm: plan.has_crm ?? false,
      has_custom_smtp: plan.has_custom_smtp ?? false,
      has_tier_program: plan.has_tier_program ?? false,
      has_customer_automation: plan.has_customer_automation ?? false,
      has_partner_network: plan.has_partner_network ?? false,
      has_ai_insights: plan.has_ai_insights ?? false,
      has_priority_support: plan.has_priority_support ?? false,
      is_active: plan.is_active
    });
    setShowModal(true);
  };

  const handleDeletePlan = (planId: number) => {
    setPlanToDelete(planId);
    setShowConfirmDelete(true);
  };

  const confirmDelete = async () => {
    if (planToDelete === null) return;
    
    try {
      await apiService.deletePlan(planToDelete);
      await loadPlans();
      setAlertMessage({ type: 'success', message: 'Plano excluído com sucesso!' });
      setTimeout(() => setAlertMessage(null), 3000);
    } catch (error) {
      console.error('Erro ao excluir plano:', error);
      setAlertMessage({ type: 'error', message: 'Erro ao excluir plano. Tente novamente.' });
      setTimeout(() => setAlertMessage(null), 5000);
    } finally {
      setShowConfirmDelete(false);
      setPlanToDelete(null);
    }
  };

  const cancelDelete = () => {
    setShowConfirmDelete(false);
    setPlanToDelete(null);
  };

  const handleNewPlan = () => {
    setEditingPlan(null);
    setFormData(initialPlan);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingPlan(null);
    setFormData(initialPlan);
    setErrors({});
  };

  const getFeatureIcon = (feature: string) => {
    const iconMap: Record<string, React.ReactNode> = {
      stores: <Building2 size={12} />,
      employees: <Users size={12} />,
      customers: <Users size={12} />,
      rewards: <Gift size={12} />,
      reports: <BarChart3 size={12} />,
      crm: <Mail size={12} />,
      smtp: <Mail size={12} />,
      tiers: <Layers size={12} />,
      automation: <Workflow size={12} />,
      partners: <Link2 size={12} />,
      ai: <Brain size={12} />,
      support: <Headphones size={12} />,
    };
    return iconMap[feature] || <Shield size={12} />;
  };

  return (
    <AdminLayout>
      <Container>
      <Header>
        <Title>Gestão de Planos</Title>
        <Button $variant="primary" onClick={handleNewPlan}>
          <Plus size={16} />
          Novo Plano
        </Button>
      </Header>

      <PlansGrid>
        {plans.map((plan) => (
          <PlanCard key={plan.id} $featured={plan.name === 'Profissional'}>
            {plan.name === 'Profissional' && <PlanBadge>Mais Popular</PlanBadge>}

            <PlanHeader>
              <PlanName>{plan.name}</PlanName>
              <PlanPrice>R$ {plan.price}<span style={{fontSize: '1rem', color: '#666'}}>/mês</span></PlanPrice>
              <PlanDescription>{plan.description}</PlanDescription>
            </PlanHeader>

            <FeaturesList>
              <FeatureItem>
                <FeatureIcon>{getFeatureIcon('stores')}</FeatureIcon>
                Até {plan.max_stores === 999 ? 'ilimitadas' : plan.max_stores} lojas
              </FeatureItem>
              <FeatureItem>
                <FeatureIcon>{getFeatureIcon('employees')}</FeatureIcon>
                Até {plan.max_employees_per_store === 999 ? 'ilimitados' : plan.max_employees_per_store} funcionários/loja
              </FeatureItem>
              <FeatureItem>
                <FeatureIcon>{getFeatureIcon('customers')}</FeatureIcon>
                Até {plan.max_customers === 99999 ? 'ilimitados' : plan.max_customers.toLocaleString()} clientes
              </FeatureItem>
              <FeatureItem>
                <FeatureIcon>{getFeatureIcon('rewards')}</FeatureIcon>
                Até {plan.max_rewards === 999 ? 'ilimitadas' : plan.max_rewards} recompensas
              </FeatureItem>
              {plan.has_advanced_reports && (
                <FeatureItem>
                  <FeatureIcon>{getFeatureIcon('reports')}</FeatureIcon>
                  Relatórios avançados
                </FeatureItem>
              )}
              {plan.has_crm && (
                <FeatureItem>
                  <FeatureIcon>{getFeatureIcon('crm')}</FeatureIcon>
                  CRM habilitado
                </FeatureItem>
              )}
              {plan.has_custom_smtp && (
                <FeatureItem>
                  <FeatureIcon>{getFeatureIcon('smtp')}</FeatureIcon>
                  SMTP personalizado
                </FeatureItem>
              )}
              {plan.has_tier_program && (
                <FeatureItem>
                  <FeatureIcon>{getFeatureIcon('tiers')}</FeatureIcon>
                  Programa de níveis (tiers)
                </FeatureItem>
              )}
              {plan.has_customer_automation && (
                <FeatureItem>
                  <FeatureIcon>{getFeatureIcon('automation')}</FeatureIcon>
                  Automações por comportamento do cliente
                </FeatureItem>
              )}
              {plan.has_partner_network && (
                <FeatureItem>
                  <FeatureIcon>{getFeatureIcon('partners')}</FeatureIcon>
                  Rede de parceiros para earn/burn
                </FeatureItem>
              )}
              {plan.has_ai_insights && (
                <FeatureItem>
                  <FeatureIcon>{getFeatureIcon('ai')}</FeatureIcon>
                  Insights com IA (churn/CLV/NBO)
                </FeatureItem>
              )}
              {plan.has_priority_support && (
                <FeatureItem>
                  <FeatureIcon>{getFeatureIcon('support')}</FeatureIcon>
                  Suporte prioritário
                </FeatureItem>
              )}
            </FeaturesList>

            <PlanActions>
              <Button onClick={() => handleEditPlan(plan)}>
                <Edit size={14} />
                Editar
              </Button>
              <Button $variant="danger" onClick={() => handleDeletePlan(plan.id)}>
                <Trash2 size={14} />
                Excluir
              </Button>
            </PlanActions>
          </PlanCard>
        ))}
      </PlansGrid>

      <Modal $show={showModal}>
        <ModalContent>
          <ModalHeader>
            <ModalTitle>
              {editingPlan ? 'Editar Plano' : 'Novo Plano'}
            </ModalTitle>
            <Button onClick={handleCloseModal}>
              <X size={16} />
            </Button>
          </ModalHeader>

          <Form onSubmit={handleSubmit}>
            <FormGroup>
              <Label>Nome do Plano *</Label>
              <Input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                placeholder="Ex: Básico, Profissional, Empresarial"
                $hasError={!!errors.name}
              />
              {errors.name && <ErrorMessage>{errors.name}</ErrorMessage>}
            </FormGroup>

            <FormGroup>
              <Label>Descrição</Label>
              <Textarea
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                placeholder="Descrição do plano..."
                $hasError={!!errors.description}
              />
              {errors.description && <ErrorMessage>{errors.description}</ErrorMessage>}
            </FormGroup>

            <FormRow>
              <FormGroup>
                <Label>Preço Mensal (R$) *</Label>
                <Input
                  type="number"
                  name="price"
                  value={formData.price}
                  onChange={handleInputChange}
                  min="0"
                  step="0.01"
                  $hasError={!!errors.price}
                />
                {errors.price && <ErrorMessage>{errors.price}</ErrorMessage>}
              </FormGroup>

              <FormGroup>
                <Label>Máximo de Lojas *</Label>
                <Input
                  type="number"
                  name="max_stores"
                  value={formData.max_stores}
                  onChange={handleInputChange}
                  min="1"
                  $hasError={!!errors.max_stores}
                />
                {errors.max_stores && <ErrorMessage>{errors.max_stores}</ErrorMessage>}
              </FormGroup>
            </FormRow>

            <FormRow>
              <FormGroup>
                <Label>Funcionários por Loja *</Label>
                <Input
                  type="number"
                  name="max_employees_per_store"
                  value={formData.max_employees_per_store}
                  onChange={handleInputChange}
                  min="1"
                  $hasError={!!errors.max_employees_per_store}
                />
                {errors.max_employees_per_store && <ErrorMessage>{errors.max_employees_per_store}</ErrorMessage>}
              </FormGroup>

              <FormGroup>
                <Label>Máximo de Clientes *</Label>
                <Input
                  type="number"
                  name="max_customers"
                  value={formData.max_customers}
                  onChange={handleInputChange}
                  min="1"
                  $hasError={!!errors.max_customers}
                />
                {errors.max_customers && <ErrorMessage>{errors.max_customers}</ErrorMessage>}
              </FormGroup>
            </FormRow>

            <FormGroup>
              <Label>Máximo de Recompensas *</Label>
              <Input
                type="number"
                name="max_rewards"
                value={formData.max_rewards}
                onChange={handleInputChange}
                min="1"
                $hasError={!!errors.max_rewards}
              />
              {errors.max_rewards && <ErrorMessage>{errors.max_rewards}</ErrorMessage>}
            </FormGroup>

            <FormRow>
              <FormGroup>
                <CheckboxLabel>
                  <Checkbox
                    type="checkbox"
                    name="has_advanced_reports"
                    checked={formData.has_advanced_reports}
                    onChange={handleInputChange}
                  />
                  Relatórios Avançados
                </CheckboxLabel>
              </FormGroup>
            </FormRow>

            <FormRow>
              <FormGroup>
                <CheckboxLabel>
                  <Checkbox
                    type="checkbox"
                    name="has_crm"
                    checked={formData.has_crm}
                    onChange={handleInputChange}
                  />
                  CRM para empresas
                </CheckboxLabel>
              </FormGroup>

              <FormGroup>
                <CheckboxLabel>
                  <Checkbox
                    type="checkbox"
                    name="has_custom_smtp"
                    checked={formData.has_custom_smtp}
                    onChange={handleInputChange}
                  />
                  SMTP personalizado
                </CheckboxLabel>
              </FormGroup>
            </FormRow>

            <FormRow>
              <FormGroup>
                <CheckboxLabel>
                  <Checkbox
                    type="checkbox"
                    name="has_tier_program"
                    checked={formData.has_tier_program ?? false}
                    onChange={handleInputChange}
                  />
                  Programa de níveis (tiers)
                </CheckboxLabel>
              </FormGroup>

              <FormGroup>
                <CheckboxLabel>
                  <Checkbox
                    type="checkbox"
                    name="has_customer_automation"
                    checked={formData.has_customer_automation ?? false}
                    onChange={handleInputChange}
                  />
                  Automações por comportamento
                </CheckboxLabel>
              </FormGroup>
            </FormRow>

            <FormRow>
              <FormGroup>
                <CheckboxLabel>
                  <Checkbox
                    type="checkbox"
                    name="has_partner_network"
                    checked={formData.has_partner_network ?? false}
                    onChange={handleInputChange}
                  />
                  Rede de parceiros
                </CheckboxLabel>
              </FormGroup>

              <FormGroup>
                <CheckboxLabel>
                  <Checkbox
                    type="checkbox"
                    name="has_ai_insights"
                    checked={formData.has_ai_insights ?? false}
                    onChange={handleInputChange}
                  />
                  Insights com IA
                </CheckboxLabel>
              </FormGroup>
            </FormRow>

            <FormRow>
              <FormGroup>
                <CheckboxLabel>
                  <Checkbox
                    type="checkbox"
                    name="has_priority_support"
                    checked={formData.has_priority_support ?? false}
                    onChange={handleInputChange}
                  />
                  Suporte prioritário
                </CheckboxLabel>
              </FormGroup>
            </FormRow>

            <FormGroup>
              <CheckboxLabel>
                <Checkbox
                  type="checkbox"
                  name="is_active"
                  checked={formData.is_active}
                  onChange={handleInputChange}
                />
                Plano Ativo
              </CheckboxLabel>
            </FormGroup>

            <FormRow style={{ marginTop: '1rem' }}>
              <Button type="button" onClick={handleCloseModal}>
                Cancelar
              </Button>
              <Button type="submit" $variant="primary">
                <Save size={16} />
                {editingPlan ? 'Atualizar' : 'Criar'} Plano
              </Button>
            </FormRow>
          </Form>
        </ModalContent>
      </Modal>

      {/* Modal de Confirmação de Exclusão */}
      <ConfirmModalOverlay $show={showConfirmDelete} onClick={cancelDelete}>
        <ConfirmBox onClick={(e) => e.stopPropagation()}>
          <ConfirmTitle>Confirmar Exclusão</ConfirmTitle>
          <ConfirmText>
            Tem certeza que deseja excluir este plano? Esta ação não pode ser desfeita.
          </ConfirmText>
          <ConfirmActions>
            <Button onClick={cancelDelete}>
              Cancelar
            </Button>
            <Button $variant="danger" onClick={confirmDelete}>
              <Trash2 size={16} />
              Excluir
            </Button>
          </ConfirmActions>
        </ConfirmBox>
      </ConfirmModalOverlay>

      {/* Alertas de Sucesso/Erro */}
      {alertMessage && (
        <AlertBox $type={alertMessage.type}>
          {alertMessage.message}
        </AlertBox>
      )}
      </Container>
    </AdminLayout>
  );
};
